<template>
  <div id="map-wrapper">
    <div class="wake-lock-indicator" :class="wakeLockBorderColor"></div>

    <!-- The openLayers map container -->
    <div id="ol-map" ref="map-root" class="z-0"></div>

    <!-- Controls-->
    <div class="ol-map-controls" ref="map-controls"></div>

    <!-- <div class="btn btn-primary absolute top-3 left-64" @click="lockViewToCenterWhenRecording">Test Center</div> -->

    <GPSButton />

    <!-- rotate-north ol-unselectable ol-control -->
    <div class="hidden">
      <div class="absolute left-2 bottom-24 cursor-pointer z-120 btn" @click="handleZoomOut">Zoom out</div>
      <div class="absolute left-2 bottom-32 cursor-pointer z-120 btn" @click="handleZoomIn">Zoom in</div>
      <div class="absolute left-2 bottom-40 cursor-pointer z-120 btn" @click="handleRotateNorth">North</div>
      <div class="absolute left-2 bottom-48 cursor-pointer z-120 btn" @click="getMyControls">Controls</div>
    </div>

    <Notification
      v-if="recording"
      :type=notificationType
      :header=notificationHeader
      :message=notificationMessage
      :offset=mainNavStatus
      :active=notificationActive
      :closeBtnVisible=notificationCloseBtnVisible
    />

    <!-- Message fields at the bottom of the screen -->
    <div class="msg mr-1" v-if="zooming">Zooming to current location</div>
    <div class="msg" v-if="recording">Recording Route: {{ recording }}</div>
  </div>
</template>

<script>
// OL Stylesheet is required for buttons!
import 'ol/ol.css'
// OL imports
import { Map, Feature } from 'ol'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { OSM, Vector as VectorSource } from 'ol/source'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import { fromLonLat } from 'ol/proj'
// Control,
import { defaults as defaultControls } from 'ol/control'
import { DragRotateAndZoom, defaults as defaultInteractions } from 'ol/interaction'
import { circular } from 'ol/geom/Polygon'
import Point from 'ol/geom/Point'
import View from 'ol/View'
import LineString from 'ol/geom/LineString'
import GeoJSON from 'ol/format/GeoJSON'
// Custom scripts imports
import Notification from '@/components/ui/Notification'
import WakeLock from '@/mixins/wakeLock'
import GPSButton from '@/components/GPSButton'
import { EventBus } from '@/eventBus'
import { easeIn, easeOut } from 'ol/easing'
import Utils from '@/utils/Utils'
import { getLength } from 'ol/sphere'

export default {
  name: 'MapContainer',
  components: { GPSButton, Notification },
  mixins: [WakeLock],
  data: () => ({
    //
    // ----- Props for the Notification component
    //
    notificationType: 'info',
    notificationHeader: 'You are not moving!',
    notificationMessage: 'Since you do not move, the app will not record any geolocation coordinates.',
    notificationActive: false,
    notificationCloseBtnVisible: false,
    //
    watchId: null,
    initialZoomIn: false,
    recordingInterval: 2500,
    intervalId: null,
    //
    olMap: null,
    geolocation: null,
    view: null,
    tileLayer: null,
    // Accuracy Feature, will be added to source as positionFeature: positionSource
    // So we do not have s sparate "acurracy source" or "accuracy layer"
    accuracyStyle: new Style({
      stroke: new Stroke({
        width: 6,
        color: [217, 150, 20, 0.8],
        lineCap: 'round'
      })
    }),
    accuracyFeature: new Feature(),
    // PositionFeature: Point that shows the current position
    positionStyle: new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({
          // orange
          color: [151, 38, 109, 1]
        }),
        stroke: new Stroke({
          color: [255, 255, 255, 1],
          width: 3
        })
      })
    }),
    positionFeature: new Feature(),
    // This source will hold positionFeature AND accuracy Feature
    // It will be added to "trackSource"
    positionSource: new VectorSource(),
    /// Layer that shows the recorded track
    /// @src: https://openlayersbook.github.io/ch10-openlayers-goes-mobile/example-04.html
    /// Create a style to display our position history (track)
    trackStyle: new Style({
      stroke: new Stroke({
        color: [151, 38, 109, 0.6],
        lineCap: 'round',
        width: 7
      })
    }),
    /// Use a single feature with a linestring geometry to display our track
    trackFeature: new Feature(),
    trackSource: new VectorSource(),
    //
    // --- this is the one and only layer for the display of the ongoing track recording
    //
    trackLayer: new VectorLayer(),
    // Used while recording / tracking
    recordTimeInterval: 2000,
    recordTimerId: null,
    recordNow: false,
    //
    firstCoord: [],
    lastCoord: [],
    currentCoord: [],
    trackedDistance: {},
    trackedSteps: null,
    //
    // ----- Other stuff
    //
    zooming: false,
    callbackParam: '',
    //
    // ----- Layers for displaying already recorded tracks ("gs" = "geojson source")
    //
    gsLayer: new VectorLayer(),
    gsSource: new VectorSource()
  }),
  computed: {
    // "Tracking" && "trackingOptions => enableHighAccuracy" is for the
    // Geolocation object of Openlayers only; not used anymore.
    // tracking: {
    //   get () { return this.$store.getters['settings/getTracking'] },
    //   set (bool) { this.$store.dispatch('settings/setTracking', bool) }
    // },
    enableHighAccuracy: {
      get () { return this.$store.getters['settings/getEnableHighAccuracy'] },
      set (bool) { this.$store.dispatch('settings/setEnableHighAccuracy', bool) }
    },
    maximumAge: {
      get () { return this.$store.getters['settings/getMaximumAge'] },
      set (bool) { this.$store.dispatch('settings/setMaximumAge', bool) }
    },
    timeout: {
      get () { return this.$store.getters['settings/getTimeout'] },
      set (bool) { this.$store.dispatch('settings/setTimeout', bool) }
    },
    geolocationPositionOptions () {
      const obj = {
        enableHighAccuracy: this.enableHighAccuracy,
        maximumAge: this.maximumAge,
        timeout: this.timeout
      }
      return obj
    },
    //
    // Geolocation recording Constraints
    //
    useMinSpeedThreshold: {
      get () { return this.$store.getters['settings/getUseMinSpeedThreshold'] }
    },
    minSpeedThreshold: {
      get () { return this.$store.getters['settings/getMinSpeedThreshold'] }
    },
    useRecordingTimeInterval: {
      get () { return this.$store.getters['settings/getUseRecordingTimeInterval'] }
    },
    recordingTimeInterval: {
      get () { return this.$store.getters['settings/getRecordingTimeInterval'] }
    },
    //
    // ----- Notification when not moving
    //
    showNotificationWhenNotMoving: {
      get () { return this.$store.getters['settings/getShowNotificationWhenNotMoving'] }
    },
    //
    // ----- Zoom levels
    //
    zoomLevelNear: {
      get () { return this.$store.getters['settings/getZoomLevelNear'] }
      // set (int) { this.$store.dispatch('settings/setZoomLevelNear', int) }
    },
    zoomLevelFar: {
      get () { return this.$store.getters['settings/getZoomLevelFar'] }
      // set (int) { this.$store.dispatch('settings/setZoomLevelFar', int) }
    },
    zoomLevelManual: {
      get () { return this.$store.getters['settings/getZoomLevelManual'] },
      set (int) { this.$store.dispatch('settings/setZoomLevelManual', int) }
    },
    //
    // Needed by updateMapData and hen pinching
    //
    currentCenter: {
      get () { return this.$store.getters['geolocation/getTemporaryCenterOfManuallyMovedMap'] },
      set (val) { return this.$store.dispatch('geolocation/setTemporaryCenterOfManuallyMovedMap', val) }
    },
    //
    // Record data for new activity
    //
    recording: {
      get () { return this.$store.getters['newActivity/getRecording'] }
      // set (bool) { this.$store.dispatch('newActivity/setRecording', bool) }
    },
    lockViewToCenter: {
      get () { return this.$store.getters['geolocation/getLockViewToCenter'] },
      set (bool) { this.$store.dispatch('geolocation/setLockViewToCenter', bool) }
    },
    // Keep these values in Vuex, that way it is easier to save the endstate of
    // a route which happens in "NewActivity", not "Container"
    totalDistance: {
      get () {
        return this.$store.getters['newActivity/getTotalDistance']
        // const obj = this.$store.getters['newActivity/getTotalDistance']
        // console.log('MapContainer totalDistance obj =', obj)
        // return obj
      },
      set (obj) {
        // console.log('MapContainer setTotalDistance obj =', obj)
        this.$store.dispatch('newActivity/setTotalDistance', obj)
      }
    },
    speed: {
      get () {
        return this.$store.getters['newActivity/getSpeed']
        // const obj = this.$store.getters['newActivity/getSpeed']
        // console.log('MapContainer getSpeed: obj =', obj)
        // return obj
      },
      set (int) {
        // console.log('MapContainer setSpeed: int =', int)
        this.$store.dispatch('newActivity/setSpeed', int)
      }
    },
    // totalSteps: {
    //   get () { return this.$store.getters['newActivity/getTotalSteps'] },
    //   set (obj) { this.$store.dispatch('newActivity/setTotalSteps', obj) }
    // },
    //
    // Show Track
    //
    showTrack: {
      get () { return this.$store.getters['trackedRoutes/getShowTrack'] },
      set (bool) { this.$store.dispatch('trackedRoutes/setShowTrack', bool) }
    },
    //
    // ---- Main menu show/hide (needed or Notification)
    //      moves the new activity window a bit to the left, when menu is shown
    //
    mainNavStatus: {
      get () { return this.$store.getters['ui/getMainNavStatus'] },
      set (bool) { this.$store.dispatch('ui/toggleMainNavStatus', bool) }
    }
  },
  mounted () {
    // this.log = 'MapContainer: mounted<br>'
    this.$nextTick(() => {
      //
      // ----- OPENLAYERS MAP-RELATED
      //
      // A view obj
      this.view = new View({
        center: [0, 0],
        constrainResolution: true, // new in v6
        // enableRotation: false,
        // rotation: -Math.PI / 2.5,
        // rotation: Math.PI / 6,
        zoom: this.zoomLevelFar
      })

      // Set style of accuracy circle feature
      this.accuracyFeature.setStyle(this.accuracyStyle)

      // Set style of position point feature
      this.positionFeature.setStyle(this.positionStyle)

      // Add accuracyFeature and positionFeature to the SAME source: positionSource
      // Will be added to trackLayer in "createPositionFeature"
      this.positionSource.addFeatures([this.positionFeature, this.accuracyFeature])

      // Initiate TrackLayer by assigning Source and Style
      this.trackLayer = new VectorLayer({
        source: this.trackSource,
        style: this.trackStyle
      })

      // The tile layer (the map)
      this.tileLayer = new TileLayer({
        source: new OSM() // tiles are served by OpenStreetMap
      })
      //
      // ----- OPENLAYERS MAP OBJECT
      //
      this.olMap = new Map({
        // Required for rotating the map
        interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
        // https://gis.stackexchange.com/questions/390608/make-rotate-button-always-visible-in-openlayers?noredirect=1#comment639778_390608
        // controls: this.controls,
        controls: defaultControls({
          rotateOptions: {
            autoHide: false,
            label: Utils.compassIcon()
          }
          // target: this.$refs['map-controls']
        }),
        // the map will be created using the 'map-root' ref
        target: this.$refs['map-root'],
        layers: [this.tileLayer],
        // the map view will initially show the whole world
        view: this.view
      })
      //
      // OpenLayers event listeners
      this.olMap.on('moveend', this.mapOnMoveEnd)
    })
    //
    // ----- EVENT BUS METHOD CALLINGS
    //
    EventBus.$on('centerMap', () => { this.centerMap() })
    EventBus.$on('deleteTrackLayer', () => { this.deleteTrackLayer() })
    EventBus.$on('toggleRecordedTrack', (geojson) => { this.toggleRecordedTrack(geojson) })
    EventBus.$on('lockViewToCenterWhenRecording', (geojson) => { this.lockViewToCenterWhenRecording(geojson) })
    EventBus.$on('removeRecordedTrack', () => { this.removeRecordedTrack() })
  },
  methods: {
    getMyControls () {
      const c = this.olMap.getControls()
      // this.$l.log('MapContainer', 'controls =', c)

      const o = this.olMap.getOverlays()
      // this.$l.log('MapContainer', 'overlays =', o)

      const i = this.olMap.getInteractions()
      // this.$l.log('MapContainer', 'interactions =', i)

      const res = this.view.getResolution()
      // this.$l.log('MapContainer', 'resolution =', res)
    },
    handleRotateNorth () {
      this.view.animate({
        rotation: 0,
        duration: 500
      })
    },
    handleZoomIn () {
      const currZoomLevel = this.view.getZoom()

      if (currZoomLevel < this.zoomLevelNear) {
        // Stop anyongoing animation
        if (this.view.getAnimating()) {
          this.view.cancelAnimations()
        }

        // start new animation
        this.olMap.getView().animate({
          zoom: currZoomLevel + 1,
          duration: 300,
          easing: easeIn
        },
        this.cbGetZoom
        )
      }
    },
    handleZoomOut () {
      const currZoomLevel = this.view.getZoom()

      if (currZoomLevel > this.zoomLevelFar) {
        // Stop anyongoing animation
        if (this.view.getAnimating()) {
          this.view.cancelAnimations()
        }

        // start new animation
        this.olMap.getView().animate({
          zoom: currZoomLevel - 1,
          duration: 300,
          easing: easeOut
        },
        this.cbGetZoom
        )
      }
    },
    cbGetZoom () {
      // this.$l.debug('MapContainer', 'cbGetZoom: this.view.getZoom() =', this.view.getZoom())
    },
    //
    // ----- DISPLAY OF A TRACKED ROUTE
    //
    /**
     * Assign the GeoJSON format to the VectorSource, the source to the layer,
     * then a style to the layer
     *
     * @invokedBy "toggleRecordedTrack"
     */
    prepareGeoJson () {
      // this.$l.debug('MapContainer', 'prepareGeoJson invoked\nthis.displayGeoJson', this.displayGeoJson)

      this.gsSource = new VectorSource({
        format: new GeoJSON()
      })

      this.gsLayer.setSource(this.gsSource)

      this.gsLayer.setStyle(new Style({
        stroke: new Stroke({
          color: this.$store.getters['ui/getTrackStrokeColor'], // == tailwind text-green-600
          width: this.$store.getters['ui/getTrackStrokeWidth']
        })
      }))
    },
    toggleRecordedTrack (geojson) {
      // this.$l.info('MapContainer', 'toggleRecordedTrack invoked\nthis.showTrack', this.showTrack)

      if (!this.showTrack) {
        // this.$l.debug('>', '> this.showTrack = false\nAdd track to map')
        /**
         * Since this loads a track anew every time, we need to prepare:
         *
         * - clear current Source: this.gsSource.clear()
         * - remove the Layer: this.olMap.removeLayer(this.gsLayer)
         * - recreate the Layer and the Source
         * - this.showTrack = false
         *
         * Probably generate the thingy here, not on "data"
         */

        // This would cause trouble, when reading the projection
        // (Solution would be to add projection to crs param "name")
        if (geojson.crs) {
          // this.$l.warn('>', '> toggleRecordedTrack', 'Found crs, deleting it')
          delete geojson.crs
        }

        if (geojson.bbox) {
          // this.$l.warn('>', '> toggleRecordedTrack', 'Found bbox, deleting it')
          delete geojson.bbox
        }

        this.prepareGeoJson(geojson)

        // "readFeature" converts to OpenLayers format
        const feature = this.gsSource.getFormat().readFeature(geojson)
        // We need to get the projections because data was recorded in EPGS:4326; OL needs EPGS:3857
        const featureProjection = this.gsSource.getFormat().readProjection(geojson)

        // convert to what OpenLayers needs
        if (featureProjection.getCode() === 'EPSG:4326') {
          // this.$l.log('MapContainer', 'toggleRecordedTrack\nfeatureProjections', featureProjection.getCode())
          feature.getGeometry().transform('EPSG:4326', 'EPSG:3857')
        }
        else {
          // this.$l.error('MapContainer', 'Projections != "EPSG:4326"! How to transform?\nfeatureProjection', featureProjection.getCode())
          // TODO: We should stop the execution of this function, right? Issue a notification

          // return false
        }

        // Clear all source features in case there were any
        this.gsSource.clear()

        // Add new track
        this.gsSource.addFeature(feature)
        // this.$l.log('MapContainer', 'toggleRecordedTrack\nthis.gsSource.getFeatures() =', this.gsSource.getFeatures())

        this.olMap.addLayer(this.gsLayer)

        // Zoom into feature
        this.olMap.getView().fit(
          feature.getGeometry().getExtent(),
          {
            maxZoom: this.zoomLevelNear,
            duration: 500,
            padding: [24, 24, 24, 24]
            // callback: this.cbViewAnimate
          }
        )

        // set showTrack to true
        this.showTrack = true

        // this.$l.log('MapContainer', 'toggleRecordedTrack\nthis.showTrack', this.showTrack)
      }
      else {
        // this.$l.warn('MapContainer', 'toggleRecordedTrack: remove track feature from source again')
        // Just clear all source features in case there were any
        this.gsSource.clear()
        this.olMap.removeLayer(this.gsLayer)
        this.showTrack = false
      }
    },
    removeRecordedTrack () {
      // this.$l.warn('MapContainer', 'removeRecordedTrack invoked')
      this.gsSource.clear()
      this.gsSource.refresh()
      this.gsLayer.unset(this.gsSource)
      this.olMap.removeLayer(this.gsLayer)
      this.showTrack = false
    },
    //
    // ----- MAP EVENTS
    //
    mapOnMoveEnd () {
      // this.$l.log('MapContainer: mapOnMoveEnd', 'invoked', '> Now call updateMap...')
      // https://openlayers.org/en/latest/examples/moveend.html
      this.updateMapData()
    },
    //
    // ----- ZOOMING
    //
    // One-time call to navigator.geolocation.getCurrentPosition() to place the
    // "location point" (positionFeature) on the map and zoom in to it.
    createPositionMarker () {
      // this.$l.info('MapContainer', 'createPositionMarker invoked', 'trackSource IS empty, get GPS, add features and zoom')

      const gl = navigator.geolocation.getCurrentPosition(pos => {
        // this.$l.debug('MapContainer', 'createPositionMarker:\nnavigator.geolocation.getCurrentPosition', pos)

        const lonLatCoords = [pos.coords.longitude, pos.coords.latitude]
        const projectedLonLatCoords = fromLonLat(lonLatCoords)

        this.createPositionFeature(projectedLonLatCoords)

        const view = this.olMap.getView()
        view.animate({
          center: projectedLonLatCoords,
          zoom: this.zoomLevelNear,
          duration: 500
        }, this.cbViewAnimate)
      }, error => {
        this.$l.error('MapContainer', 'createPositionMarker\nError', error.id + ': ' + error.message)
      }, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      })
    },
    createPositionFeature (coords) {
      // this.$l.info('MapContainer', 'createPositionFeature invoked\ncoords', coords)

      let layerExists = false
      let existingLayer = null

      // Check if this.trackLayer already exists; "allLayers" = OL's "Collection"
      const allLayers = this.olMap.getLayers()
      allLayers.forEach(l => {
        if (l.ol_uid === this.trackLayer.ol_uid) {
          layerExists = true
          existingLayer = l
        }
      })

      // If this.trackLayer does not yet exist, create it
      if (!layerExists) {
        // this.$l.warn('MapContainer', 'createPositionFeature\ntrackLayer does not yet exist', 'create trackLayer by adding Point feature, setting layer source and adding layer to map')

        // The style of the positioFeature (point) has already been set in "mounted()"
        this.positionFeature.setGeometry(coords ? new Point(coords) : null)

        // Add positionSource (accuracyFeature + positonFeature) to tracklayer
        this.trackLayer.setSource(this.positionSource)

        // Add layer to map
        this.olMap.addLayer(this.trackLayer)
      }
      else {
        // this.$l.warn('MapContainer', 'createPositionFeature\nlayer already exists, existingLayer =', existingLayer)
      }
    },
    centerMap (param) {
      // this.$l.info('MapContainer', 'centerMap invoked\nparam =', param)

      // Set zooming to true as long as zooming is going on; reset in callback
      this.zooming = true

      // Set a callback parameter to handle further actions once the animation
      // has finished (see cbViewAnimate)
      this.callbackParam = param

      if (!this.trackSource.isEmpty()) {
        // this.$l.debug('MapContainer', 'centerMap', 'trackSource is NOT empty, zoom to extend')
        // When watchPosition is active...
        // size / padding / nearest / minResolution / maxZoom / duration / easing / callback
        this.olMap.getView().fit(
          this.trackSource.getExtent(),
          {
            maxZoom: this.zoomLevelNear,
            duration: 500,
            padding: [12, 12, 24, 12],
            callback: this.cbViewAnimate
          }
        )
      }
      else {
        // this.$l.log('MapContainer', 'centerMap', 'trackSource IS empty, zoom in and create position marker')
        this.createPositionMarker()
      }
    },
    cbViewAnimate (e) {
      // this.$l.info('MapContainer', 'cbViewAnimate invoked', 'e = ' + e)

      this.zooming = false
      this.zoomLevelManual = this.zoomLevelNear
      this.updateMapData()

      // If the callbackParam ist set, invoke the appropriate action
      if (this.callbackParam === 'watchPosition') {
        this.watchPosition()
        // Reset callback param again
        this.callbackParam = ''
      }
    },
    updateMapData () {
      // this.$l.info('MapContainer', 'updateMapData invoked', '')
      this.zoomLevelManual = this.olMap.getView().getZoom()
      this.currentCenter = this.olMap.getView().getCenter()
    },
    cbLockViewToCenterWhenRecordingZoom () {},
    /**
     * Keep the map centered using the positionFeature as center point
     *
     * @invokedBy "watchPosition"
     * @todo: animate the changes
     */
    lockViewToCenterWhenRecording () {
      // this.$l.info('mapContainer', 'lockViewToCenterWhenRecording invoked', '')

      if (this.$store.getters['geolocation/getLockViewToCenter']) {
        // this.$l.success('MapContainer', 'lockViewToCenter = TRUE, so center map', '')

        if (!this.zooming && this.initialZoomIn) {
          // this.$l.success('MapContainer', 'Zooming is false, so center map now', '')

          this.olMap.getView().fit(
            // this.trackSource.getExtent(),
            // We want to center the map around the PositionFeature, not the complete track
            this.positionFeature.getGeometry().getExtent(),
            {
              duration: 750,
              padding: [24, 24, 24, 24],
              maxZoom: this.$store.getters['settings/getZoomLevelNear']
              // nearest: true,
              // callback: this.cbLockViewToCenterWhenRecordingZoom
            }
          )
        }
      }
    },
    //
    // ----- GEOLOCATION RECORDING, WATCH and DRAW
    //
    /**
     * Calculate the length of the current line
     *
     * @invokedBy "getGPSData"
     */
    calculateDistance () {
      /**
       * Calculate distance while geolocation is watching
       * @src https://openlayers.org/en/latest/examples/measure.html
       */
      const geom = this.trackFeature.getGeometry()

      // Save the distance to a computed property that is connected to VueX
      // Since it is an object, reactivity might only be available, when assigning
      // a completely new obj
      this.totalDistance = Object.assign({}, Utils.formatLength(geom))

      // // Other useful data:
      // this.currentCoord = geom.getLastCoordinate()
      // this.firstCoord = geom.getFirstCoordinate()
      // this.lastCoord = geom.getLastCoordinate()
    },
    /**
     * Get one Geolocation datum, when navigator's watch is active
     * (which means: we are recording an activity)
     *
     * @invokedBy "capturePosition"
     */
    getGPSData (pos) {
      // this.$l.debug('MapContainer','getGPSData: pos =', pos)
      /**
       * @tutorial
       * Transform to projection View
       *
       * @example
       * const coordinates = fromLonLat([pos.coords.longitude, pos.coords.latitude])
       *
       * @summary
       * KEEP THIS COMMENT AS A REMINDER!
       *
       * @description
       * With this, it would be possible to convert the coordinates right away to
       * the format OpenLayers needs. Just substitute the "pos.coords.longitude"
       * (and ...latitude) vars with "coordinates[1]" (..an [0]).
       * BUT - that would mean we would not have a re-usable geoJson data file to
       * export (or transform the values there before export...)
       *
       * Currently we save in GeoJSON native format (EGPS:4326) and transform
       * the coordinates to EGPS:38.. in "showTrack()".
       */

      // First calculate distance
      this.calculateDistance()

      // geojson data conform (max 3 values)
      // (routeObj.geojson = objArr)
      const objArr = [
        // already in proper lon-lat order
        pos.coords.longitude,
        pos.coords.latitude,
        pos.coords.altitude
      ]

      // Extended geojson, to be put on "properties" in geojsonObj
      // (routeObj.geojson.properties[coordinatesExt] = objArrExt)
      const objArrExt = [
        // already in proper lon-lat order
        pos.coords.longitude,
        pos.coords.latitude,
        pos.coords.altitude,
        Date.now(),
        pos.coords.heading,
        pos.coords.speed,
        pos.coords.accuracy,
        pos.coords.altitudeAccuracy,
        // status of recording (TODO: eventually we want to calculate how much
        // of the total time is set to "paused")
        this.recording,
        // Distance obj (in m, km, miles, seamiles, inches, feet)
        this.totalDistance
      ]

      return [objArr, objArrExt]
    },
    /**
     * Continuousely draw the tracked route
     *
     * @invokedBy "watchPosition"
     */
    drawPositionAndTrack (lonLatCoords, projectedLonLatCoords, pos) {
      // Draw a Point at the given coordinate
      this.positionFeature.setGeometry(projectedLonLatCoords ? new Point(projectedLonLatCoords) : null)

      // Draw a Circle, that indicates the accuracy, at the given coordinate
      const accuracy = circular(lonLatCoords, pos.coords.accuracy)
      this.accuracyFeature.setGeometry(accuracy.transform('EPSG:4326', this.olMap.getView().getProjection()))

      // Draw a LineString by appending the current coordinates
      this.trackFeature.getGeometry().appendCoordinate(projectedLonLatCoords)
    },
    /**
     * Capture position when all settings allow it
     * (recording constraints etc)
     *
     * 2 datasets are prepared:
     *
     * gpsDataset[0]: minimal version – contains only 3 values: lat, long,
     *                altitude (to be saved at "geojson.geometry")
     * gpsDataset[1]: extended version – contains many values (so be saved at
     *                "geojson.properties")
     *
     * @invokedBy "watchPosition"
     */
    capturePosition (pos) {
      // get geolocation data sets
      const gpsDataSet = this.getGPSData(pos)

      // Get coords for drawing dot, circle and line
      const lonLatCoords = [gpsDataSet[0][0], gpsDataSet[0][1]]

      // Convert to OpenLayers coords
      const projectedLonLatCoords = fromLonLat(lonLatCoords)

      // For display on screen use extended version (this is overwritten each time)
      // The "mutation" in store/geolocation assigns attributes to this data.
      this.$store.dispatch('geolocation/setCurrentGeolocation', gpsDataSet[1])

      // For continuous (accumulative) saving
      this.$store.dispatch('newActivity/saveTmpCoords', [gpsDataSet[0], gpsDataSet[1]])

      // Set very first coordinate
      // TODO: Maybe draw a separate symbol at firstCoord to indicate the start point of the track
      if (this.firstCoord.length < 1) {
        this.firstCoord.push({
          'EPGS:4326': lonLatCoords,
          'EPGS:3857': projectedLonLatCoords
        })
      }

      this.drawPositionAndTrack(lonLatCoords, projectedLonLatCoords, pos)
      this.lockViewToCenterWhenRecording(projectedLonLatCoords)

      // We are moving, so deactivate any potential "Not moving" notification
      // (could be deactiated anyway via "Settings", but nevertheless...)
      this.notificationActive = false
    },
    /**
     * @invokedBy "startRecording"
     * @description THIS IS WAY TOO COMPLIATED!!!
     */
    watchPosition () {
      // // this.$l.info('MapContainer', 'watchPosition invoked', '')
      this.activateScreenWakeLock()
      this.prepareTrackFeature()

      /**
       * Start time interval
       *
       * @description
       * Save geolocation position object only in certrain timeIntervals when
       * activated. This should help to reduce data. Creates a "recordTimerId",
       * which sets the vaiable "recordNow to true after a given amount of time.
       * This vaiable is set to false again once the GPS data was saved. It will
       * be set to true again, once the timeInterval has elapsed again....
       * The "recorTimerId" must be cleared in "stopRecording" again.
       *
       * @todo
       * Check if this could be done with the "cache" param of the 3rd
       * geolocation option.
       *
       * @warning
       * Currently, this function is way to complicated!
       */
      if (this.useRecordingTimeInterval) {
        // this.$l.log('MapContainer', 'watchPosition: setup recordTimerID (setInterval)\nthis.useRecordingTimeInterval', this.useRecordingTimeInterval)
        this.recordTimerId = setInterval(() => {
          this.recordNow = true
        }, this.recordTimeInterval)
      }
      else {
        // this.$l.log('MapContainer', 'watchPosition: We are not using "recordingTimeInterval', '')
      }

      /**
       * The navigator's geolocation object
       *
       * @description
       * The continuous reading of the incoming GPS signal is created on a
       * "this.watchId", which is reset on "stopRecording". The signal is coming
       * in whenever GPS is recognized. There is no way to control its frequency.
       * Therefore 2 parameters, a time interval that defines when GPS data will
       * be saved, and
       */
      this.watchId = navigator.geolocation.watchPosition(pos => {
        // Check if timer allows for a recoring of data now... (see setInterval above)
        // This exists in order to reduce the amount of recorded data
        switch (this.useRecordingTimeInterval) {
          case true: {
            // this.$l.log('MapContainer', 'watchPosition: Switch 1:TRUE – this.useRecordTimeInterval =', this.useRecordingTimeInterval)

            switch (this.recordNow) {
              case true: {
                // this.$l.debug('MapContainer', 'watchPosition: Switch 2:TRUE – this.recordNow =', this.recordNow)

                // reset recordNow
                this.recordNow = false

                // Only record, when the speed is over a certain value (0.9 seems to be good: 0.9 m/sec)
                // This also exists in order to reduce the amount of recorded data
                // TODO: use xSate for this scenario?
                switch (this.useMinSpeedThreshold) {
                  case true: {
                    // this.$l.debug('MapContainer', 'watchPosition: Switch 3:TRUE – this.usesMinSpeedThreshold =', this.useMinSpeedThreshold)

                    switch (pos.coords.speed > this.minSpeedThreshold) {
                      case true: {
                        // this.$l.debug('MapContainer', 'watchPosition: Switch 4:TRUE – capture data, we are moving fast enough...')

                        // Get gpsData
                        this.capturePosition(pos)

                        break
                      }

                      case false: {
                        // this.$l.debug('MapContainer', 'watchPosition: SWITCH 4:FALSE – do NOT capture data, we are moving TOO SLOW!!!')
                        // // this.$l.warn('MapContainer', 'startRecording: Speed <= threshold', 'pos.coords.speed = ' + pos.coords.speed)

                        switch (this.showNotificationWhenNotMoving) {
                          case true: {
                            // this.$l.log('MapContainer', 'watchPosition - showNotificationIfNotMoving: SWITCH 5:TRUE – this.showNotificationWhenNotMoving =', this.showNotificationWhenNotMoving)

                            setTimeout(() => {
                              this.notificationActive = true
                            }, 1000)

                            break
                          }

                          case false: {
                            // this.$l.log('MapContainer', 'watchPosition - showNotificationIfNotMoving: SWITCH 5:false – this.showNotificationWhenNotMoving =', this.showNotificationWhenNotMoving)
                            break
                          }

                          default: {
                            this.$l.fatal('MapContainer', 'watchPosition - showNotificationIfNotMoving: SWITCH 5 – NOT RESULT! This is an ERROR!')
                          }
                        }

                        break
                      }

                      default: {
                        // this.$l.fatal('MapContainer', 'watchPosition: SWITCH 4 – NOT RESULT! This is an error!')
                      }
                    }

                    break
                  }

                  case false: {
                    // this.$l.debug('MapContainer', 'watchPosition: Switch 3:FALSE – this.usesMinSpeedThreshold =', this.useMinSpeedThreshold)
                    this.capturePosition(pos)
                    break
                  }

                  default: {
                    // this.$l.fatal('MapContainer', 'watchPosition: Switch 3 – NOT RESULT! This is an error!')
                  }
                }

                break
              }

              case false: {
                // this.$l.debug('MapContainer', 'watchPosition: Switch 2:FALSE – this.recordNow =', this.recordNow)
              }
            }

            break
          }

          case false: {
            // this.$l.log('MapContainer', 'watchPosition: Switch 1:FALSE – this.useRecordTimeInterval =', this.useRecordingTimeInterval)

            // Only record, when the speed is over a certain value (0.9 seems to be good: 0.9 m/sec)
            // This also exists in order to reduce the amount of recorded data
            // TODO: use xSate for this scenario?
            switch (this.useMinSpeedThreshold) {
              case true: {
                // this.$l.log('MapContainer', 'watchPosition: Switch 6:TRUE – this.usesMinSpeedThreshold =', this.useMinSpeedThreshold)

                switch (pos.coords.speed > this.minSpeedThreshold) {
                  case true: {
                    // this.$l.warn('MapContainer', 'watchPosition: Switch 7:TRUE – Speed <= threshold', 'pos.coords.speed = ' + pos.coords.speed)
                    // Get gpsData
                    this.capturePosition(pos)

                    break
                  }

                  case false: {
                    // this.$l.warn('MapContainer', 'watchPosition: Switch 7:FALSE – Speed <= threshold', 'pos.coords.speed = ' + pos.coords.speed)

                    if (this.showNotificationWhenNotMoving) {
                      setTimeout(() => {
                        this.notificationActive = true
                      }, 1000)
                    }

                    break
                  }

                  default: {
                    // this.$l.fatal('MapContainer', 'startRecording: Switch 7 – NOT VALUE!!! This is an error!\nSpeed <= threshold', 'pos.coords.speed = ' + pos.coords.speed)
                  }
                }

                break
              }

              case false: {
                // this.$l.log('MapContainer', 'watchPosition: Switch 6:FALSE – this.usesMinSpeedThreshold =', this.useMinSpeedThreshold)
                this.capturePosition(pos)
                break
              }

              default: {
                // this.$l.log('MapContainer', 'watchPosition: Switch 6– NOT VALUE!!!! This is an error!')
              }
            }

            break
          }

          default: {
            // this.$l.log('MapContainer', 'watchPosition: Switch 1 – NO VALUE!!!! This is an error!')
          }
        }
      },
      this.watchPositionError,
      this.geolocationPositionOptions
      )
    },
    watchPositionError (error) {
      this.$l.error('MapContainer', 'startRecording', 'Error', error.id + ': ' + error.message)
    },
    /**
     * Activate the OpenLayers features that will show the track
     *
     * @invokedBy "startRecording"
     */
    prepareTrackFeature () {
      // Remove trackLayer here, 'cause it could have been added in "centerMap"
      this.olMap.removeLayer(this.trackLayer)

      // Done in "mounted()"
      // this.accuracyFeature.setStyle(this.accuracyStyle)
      this.trackFeature.setGeometry(new LineString([]))

      // Clear source
      this.trackSource.clear()
      // this.trackSource.refresh()
      this.trackSource.addFeatures([
        this.accuracyFeature,
        this.positionFeature,
        this.trackFeature
      ])

      this.trackLayer.setSource(this.trackSource)
      this.trackLayer.setStyle(this.trackStyle)

      // Add track layer again
      this.olMap.addLayer(this.trackLayer)
    },
    //
    // ----- RECORDING START - STOP - PAUSE - RESUME
    //       Main recording data is handled in NewActivity.vue
    //
    startRecording () {
      // this.$l.log('MapContainer', 'startRecording invoked', 'this.recording =' + this.recording)

      this.resetParams()

      // Zoom in; once zoomed in, the callback invokes "watch"
      if (!this.initialZoomIn) {
        this.centerMap('watchPosition')
        this.initialZoomIn = true
      }
    },
    stopRecording () {
      // // this.$l.info('MapContainer: stopRecording', 'this.recording', this.recording)
      // // this.$l.log('>', 'newActivity not yet saved nor deleted!')
      this.resetParams()
      this.deactivateScreenWakeLock()

      // TODO: Assign current* data to total* (like meters, km, steps...)
      this.totalDistance = this.currentDistance

      // reset current data
      this.currentDistance = {}

      // Get one last coordinate, just to be sure
      // TODO: SHOULD WE REALLY DO THAT??? CURRENTLY IT IS WRONG: values missing
      //       (distance, steps, but also sth in between, find out what)
      // TODO: Do the same "last fetch" for "step counter", once it is integrated
      navigator.geolocation.getCurrentPosition(
        pos => {
          const objArr = [
            pos.coords.longitude,
            pos.coords.latitude,
            pos.coords.altitude
          ]

          const objArrExt = [
            pos.coords.longitude,
            pos.coords.latitude,
            pos.coords.altitude,
            Date.now(),
            pos.coords.heading,
            pos.coords.speed,
            pos.coords.accuracy,
            pos.coords.altitudeAccuracy,
            // status of recording (TODO: eventually we want to calculate how much
            // of the total time is set to "paused")
            this.recording,
            // Distance obj in m, km, miles, seamiles, feet, inches
            this.totalDistance
          ]

          // Assign values to last coordinate
          // TODO: Maybe use this to indicate the end point of the track?
          // this.lastCoord.push([pos.coords.longitude, pos.coords.latitude])

          this.$store.dispatch('newActivity/saveTmpCoords', [objArr, objArrExt])
        },
        error => {
          this.log = 'MapContainer: stopRecording - lastCoords - getCurrentPosition error = ' + error + '<br>'
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      )
    },
    // Used when a recording was stopped and a new one will start. (Not used
    // to remove a track layer; for that see above "removeRecordedTrack")
    deleteTrackLayer () {
      // this.$l.warn('MapContainer', 'deleteTrackLayer invoked', '')
      // See "stopRecording" for other settings
      this.accuracyFeature.unset('geometry')
      this.trackFeature.unset('geometry')
      this.trackSource.clear()
      this.trackLayer.unset(this.trackSource)
      this.olMap.removeLayer(this.trackLayer)

      // TODO: create a position marker
      this.createPositionMarker()
    },
    //
    // ----- Screen Wake Lock (see mixins)
    //
    activateScreenWakeLock () {
      // if wakelock is allowed in settings...
      if (this.$store.getters['settings/getScreenWakeLockAvailability']) {
        // ... turn it on, when recording starts
        this.requestWakeLock()
      }
      else {
        // this.$l.error('MapContainer:startRecording', 'wakeLock is NOT allowed (see "Settings" menu)')
      }
    },
    deactivateScreenWakeLock () {
      // if wakelock is allowed in settings, turn it on, when recording starts
      if (this.$store.getters['settings/getScreenWakeLockAvailability']) {
        if (this.wakeLockSentinel) {
          // this.$l.debug('MapContainer', 'deactivateScreenWakeLock: wake-lock sentinel to release found')

          this.releaseWakeLock()
        }
        else {
          // this.$l.warn('MapContainer', 'deactivateScreenWakeLock: no wake-lock sentinel to relase found')
        }
      }
      else {
        // this.$l.error('MapContainer:startRecording', 'wakeLock is NOT allowed (see "Settings" menu)')
      }
    },
    //
    // ----- HELPER FUNCTIONS
    //
    resetParams () {
      // In case these values are still set from a previous recording, reset them here
      // this is good habit, since the user could have simply not saved a previous recording
      // and start a new one again... (@TODO: move to extra function)
      this.currentCoord = []
      this.lastCoord = []
      this.firstCoord = []
      this.initialZoomIn = false
      this.lockViewToCenter = true
      // this.trackedDistance = []
      this.speed = 0
      this.totalDistance = []
      navigator.geolocation.clearWatch(this.watchId)
      this.watchId = null
      // this.$l.info('MapContainer', 'resetParams\nthis.watchId', this.watchId)

      if (this.useRecordingTimeInterval) {
        clearInterval(this.recordTimerId)
        this.recordTimerId = null
        // this.$l.info('MapContainer', 'resetParams\nthis.recordTimerId', this.recordTimerId)
      }

      // Notification
      if (this.showNotificationWhenNotMoving) {
        this.notificationActive = false
      }
    }
  },
  watch: {
    // Do not use an arrow function here
    recording: function (newVal, oldVal) {
      // // this.$l.log('MapContainer: watch', 'recording oldVal', oldVal)
      // // this.$l.log('MapContainer: watch', 'recording newVal', newVal)
      if (newVal === 'paused') {
        // this.pauseRecording()
      }
      else if (newVal === false) {
        this.stopRecording()
      }
      else {
        this.startRecording()
      }
    }
  }
}
</script>

<style lang="postcss">

#map-wrapper,
#ol-map {
  @apply absolute inset-0 flex items-end justify-center w-screen h-screen min-h-full max-h-full overflow-hidden box-border border-none
}

/**
 * ----- OpenLayers Classes
 */

/*
.ol-overlaycontainer-stopevent {
  @apply absolute top-24 left-3 w-1/2 h-12;
  @apply border border-pink-800;
  @apply z-30;
}
*/

.ol-control {
  @apply rounded;
}

.ol-control,
.ol-control:hover {
  @apply bg-transparent;
}
.ol-control button,
.ol-touch .ol-control button {
  @apply rounded text-xl bg-pink-800;
}
.ol-control button:active,
.ol-control button:focus,
.ol-control button:hover {
  @apply bg-pink-700;
}

.ol-rotate {
  left: 0.5rem;
  top: 4.75rem;
  @apply rounded-full;
}
.ol-rotate.ol-control button {
  @apply flex items-center justify-center rounded-full;
}

.ol-rotate .ol-compass {
  position: relative;
  display: inline-block !important;
}

.ol-zoom {
  top: 6.85rem;
}

.ol-zoom .ol-zoom-in,
.ol-zoom .ol-zoom-out,
.ol-control button {
  @apply rounded;
}

.ol-attribution,
.ol-attribution.ol-uncollapsible {
  font-size: 10px;
  font-family: Verdana, sans-serif;
  padding-top: .25em;
  left: 0;
  right: auto;
  text-align: text-left;
}

.ol-attribution:not(.ol-collapsed) {
  background: rgba(255,255,255,.75);
}

.ol-attribution li {
  @apply text-pink-800;
}

.msg {
  @apply text-pink-800 z-10 relative px-2 py-1 mb-3 lg:mb-2 font-light bg-white bg-opacity-75 rounded-md text-xs;
}

/**
 * Blue 9-step color palette from Coloors
 * @src https://coolors.co/03045e-023e8a-0077b6-0096c7-00b4d8-5bcfe6-90e0ef-ade8f4-caf0f8
 */
--navy-blue: #03045eff;
--dark-cornflower-blue: #023e8aff;
--star-command-blue: #0077b6ff;
--blue-green: #0096c7ff;
--cerulean-crayola: #00b4d8ff;
--sky-blue-crayola: #5bcfe6ff;
--sky-blue-crayola-2: #90e0efff;
--blizzard-blue: #ade8f4ff;
--powder-blue: #caf0f8ff;

--navy-blue: hsla(239, 94%, 19%, 1);
--dark-cornflower-blue: hsla(214, 97%, 27%, 1);
--star-command-blue: hsla(201, 100%, 36%, 1);
--blue-green: hsla(195, 100%, 39%, 1);
--cerulean-crayola: hsla(190, 100%, 42%, 1);
--sky-blue-crayola: hsla(190, 74%, 63%, 1);
--sky-blue-crayola-2: hsla(189, 75%, 75%, 1);
--blizzard-blue: hsla(190, 76%, 82%, 1);
--powder-blue: hsla(190, 77%, 88%, 1);

$navy-blue: #03045eff;
$dark-cornflower-blue: #023e8aff;
$star-command-blue: #0077b6ff;
$blue-green: #0096c7ff;
$cerulean-crayola: #00b4d8ff;
$sky-blue-crayola: #5bcfe6ff;
$sky-blue-crayola-2: #90e0efff;
$blizzard-blue: #ade8f4ff;
$powder-blue: #caf0f8ff;

$navy-blue: hsla(239, 94%, 19%, 1);
$dark-cornflower-blue: hsla(214, 97%, 27%, 1);
$star-command-blue: hsla(201, 100%, 36%, 1);
$blue-green: hsla(195, 100%, 39%, 1);
$cerulean-crayola: hsla(190, 100%, 42%, 1);
$sky-blue-crayola: hsla(190, 74%, 63%, 1);
$sky-blue-crayola-2: hsla(189, 75%, 75%, 1);
$blizzard-blue: hsla(190, 76%, 82%, 1);
$powder-blue: hsla(190, 77%, 88%, 1);

$navy-blue: rgba(3, 4, 94, 1);
$dark-cornflower-blue: rgba(2, 62, 138, 1);
$star-command-blue: rgba(0, 119, 182, 1);
$blue-green: rgba(0, 150, 199, 1);
$cerulean-crayola: rgba(0, 180, 216, 1);
$sky-blue-crayola: rgba(91, 207, 230, 1);
$sky-blue-crayola-2: rgba(144, 224, 239, 1);
$blizzard-blue: rgba(173, 232, 244, 1);
$powder-blue: rgba(202, 240, 248, 1);

$gradient-top: linear-gradient(0deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-right: linear-gradient(90deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-bottom: linear-gradient(180deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-left: linear-gradient(270deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-top-right: linear-gradient(45deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-bottom-right: linear-gradient(135deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-top-left: linear-gradient(225deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-bottom-left: linear-gradient(315deg, #03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);
$gradient-radial: radial-gradient(#03045eff, #023e8aff, #0077b6ff, #0096c7ff, #00b4d8ff, #5bcfe6ff, #90e0efff, #ade8f4ff, #caf0f8ff);

</style>
